import React from 'react';
import CustomButton from '../Components/Buttons'; 
import Textbox from '../Components/Textbox'; 
import Dropdown from '../Components/Dropdown';
import countries from '../json/CountriesHispanic.json'; 
import languages from '../json/Languages.json';

export const generateSurveyComponents = (surveyData, onAnswerChange, answers, currentCategory, t, i18n, highlightInvalid = false) => {
  const categorizedQuestions = surveyData.Survey.reduce((acc, question) => {
    acc[question.Category] = acc[question.Category] || [];
    acc[question.Category].push(question);
    return acc;
  }, {});

  const questionsToRender = categorizedQuestions[currentCategory] || [];

  const countryOptions = countries;
  const languageOptions = languages.map(lang => ({
    value: lang.name,
    label: lang.name,
  }));

  const getBorderStyle = (isInvalid) => {
    if (highlightInvalid && isInvalid) {
      return {
        borderColor: 'red !important',
      };
    }
  };

  const tagDropdowns = {
    '{grade}': ['Kindergarten', 'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5','Grade 6','Grade 7', 'Grade 8'],
    '{country}': countryOptions.map(country => ({
      value: country.name,
      label: country.name,
    })),
    '{yes/no}': ['Yes', 'No'],
    '{language}': languageOptions, // Language dropdown
  };

  const replaceTagsWithDropdowns = (text) => {
    const parts = text.split(/(\{.*?\}|\n)/g);
  
    return parts.map((part, idx) => {
      if (part === '\n') {
        return <br key={`br-${idx}`} />;
      } else if (tagDropdowns[part]) {
        //Need to handle specfic question
        if (text === "¿Ha asistido su hijo/a a la escuela antes? {yes/no} {grade} Si es así, ¿en qué país? {country}") {
          const dropdownType = part.replace(/[{}]/g, ''); 
          const currentValue = answers[text]?.[dropdownType] || '';
          
          return (
            <span key={`dropdown-${idx}`} style={{ display: 'inline-flex', alignItems: 'center' }}>
              <Dropdown
                options={tagDropdowns[part]}
                value={currentValue}
                onChange={(value) => onAnswerChange(text, { [dropdownType]: value })}
                style={{ 
                  marginLeft: '5px',
                }}
              />
            </span>
          );
        }
        
        //Other questions with dropdowns
        const answerKey = text.includes("school year") ? 
          `${text}-${part === '{grade}' ? 'Grade' : 'Country'}` : 
          text;
        const isInvalid = highlightInvalid && (!answers[answerKey]);
        
        return (
          <span key={`dropdown-${idx}`} style={{ display: 'inline-flex', alignItems: 'center' }}>
            <Dropdown
              options={tagDropdowns[part]}
              value={answers[answerKey] || ''} 
              onChange={(value) => onAnswerChange(answerKey, value)}
              style={{ 
                marginLeft: '5px',
                ...getBorderStyle(isInvalid),
              }}
            />
          </span>
        );
      } else {
        //Normal question
        return <span key={`text-${idx}`}>{part}</span>;
      }
    });
  };

  return questionsToRender.map((question, index) => {
    const isInvalid = !answers[question.Question]

    switch (question.Type) {
      case 'Text Area':
        return (
          <div key={index}>
            <h3 style={{
              color: 'var(--primary-color)', 
              fontWeight: 'bold',
              fontSize: '1.2rem',
              textAlign: 'left',
              marginBottom: '10px',
            }}>
              {question.Question}
            </h3>
            <Textbox
              placeholder={t('textplaceholder')}
              value={answers[question.Question] || ''}
              onChange={(value) => onAnswerChange(question.Question, value)}
              style={{
                ...getBorderStyle(isInvalid),
              }}
            />
          </div>
        );

        case 'Dropdown':
          return (
            <div key={index}>
              <h3 style={{
                color: 'var(--primary-color)', 
                fontWeight: 'bold',
                fontSize: '1.2rem',
                textAlign: 'left',
                marginBottom: '10px',
              }}>
                {question.Question.includes('{') ? 
                  replaceTagsWithDropdowns(question.Question) : 
                  question.Question}
              </h3>
              {!question.Question.includes('{') && (
                <div style={{ textAlign: 'left' }}> 
                  <Dropdown
                    options={[
                      ...(question.dropdowns?.[0]?.id === 'country' ? tagDropdowns['{country}'] :
                        question.dropdowns?.[0]?.id === 'language' ? tagDropdowns['{language}'] :
                        question.dropdowns?.[0]?.options || [])
                    ]}
                    value={answers[question.Question] || ''}
                    onChange={(value) => onAnswerChange(question.Question, value)}
                    style={{
                      outline: 'none',
                    }}
                    wrapperStyle={{
                      ...getBorderStyle(highlightInvalid && (!answers[question.Question] || answers[question.Question] === '')),
                    }}
                  />
                </div>
              )}
            </div>
          );

      case 'Multiple Dropdown':
        return (
          <div key={index}>
            <h3 style={{
              color: 'var(--primary-color)', 
              fontWeight: 'bold',
              fontSize: '1.2rem',
              textAlign: 'left',
              marginBottom: '10px',
            }}>
              {replaceTagsWithDropdowns(question.Question)}
            </h3>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}> 
              {question.Options.map((opt, optIndex) => {
                const isDropdownInvalid = !answers[`${question.Question}-${optIndex}`] || answers[`${question.Question}-${optIndex}`] === 'Select an option';
                return (
                  <Dropdown
                    key={`${index}-${optIndex}`}
                    options={[
                      { value: '', label: 'Select an option' },
                      ...(question.dropdowns?.[optIndex]?.id === 'country' ? tagDropdowns['{country}'] :
                        question.dropdowns?.[optIndex]?.id === 'language' ? tagDropdowns['{language}'] :
                        question.dropdowns?.[optIndex]?.options || [])
                    ]}
                    value={answers[`${question.Question}-${optIndex}`] || 'Select an option'}
                    onChange={(value) => onAnswerChange(`${question.Question}-${optIndex}`, value)}
                    style={{
                      ...getBorderStyle(isDropdownInvalid),
                    }}
                  />
                );
              })}   
            </div> 
          </div>
        );

      case 'Single Choice':
        return (
          <div key={index}>
            <h3 style={{
              color: 'var(--primary-color)', 
              fontWeight: 'bold',
              fontSize: '1.2rem',
              textAlign: 'left',
              marginBottom: '10px',
            }}>
              {question.Question}
            </h3>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {question.Options.map((optionObj, optIndex) => (
                <CustomButton
                  key={optIndex}
                  label={optionObj.Option}
                  type="response"
                  onClick={() => onAnswerChange(question.Question, optionObj.Option)}
                  style={{
                    backgroundColor: answers[question.Question] === optionObj.Option ? 'var(--primary-color)' : 'var(--primary-white-color)',
                    color: answers[question.Question] === optionObj.Option ? 'var(--primary-white-color)' : 'var(--primary-color)', 
                    ...getBorderStyle(isInvalid), border: '3px solid var(--primary-color)',
                  }}
                />
              ))}
            </div>
          </div>
        );

      default:
        return <p key={index}>Unsupported Question Type</p>;
    }
  });
};
